import { z } from "zod";
import {
  createSkillMemory,
  type SkillMemory,
  type SkillMemoryEntry,
} from "@/domain/learning/skill-memory";
import type { SkillMemoryRepository } from "@/domain/learning/skill-memory-repository";

const STORAGE_KEY = "vimquest.skill-memory.v1";

const entrySchema = z
  .object({
    lastPracticedAt: z.string(),
    successes: z.number().int().positive(),
  })
  .strict();

const persistedSchema = z
  .object({
    schemaVersion: z.literal(1),
    entries: z.record(z.string(), entrySchema),
  })
  .strict();

function toPersisted(memory: SkillMemory): z.infer<typeof persistedSchema> {
  const entries: Record<string, SkillMemoryEntry> = {};
  for (const [key, entry] of memory.entries) {
    entries[key] = entry;
  }
  return { schemaVersion: 1, entries };
}

function toDomain(data: z.infer<typeof persistedSchema>): SkillMemory {
  return { entries: new Map(Object.entries(data.entries)) };
}

export class LocalStorageSkillMemoryRepository implements SkillMemoryRepository {
  constructor(private readonly storage: Storage = window.localStorage) {}

  load(): SkillMemory {
    const raw = this.storage.getItem(STORAGE_KEY);
    if (raw === null) {
      return createSkillMemory();
    }
    try {
      const parsed: unknown = JSON.parse(raw);
      const result = persistedSchema.safeParse(parsed);
      if (!result.success) {
        return createSkillMemory();
      }
      return toDomain(result.data);
    } catch {
      return createSkillMemory();
    }
  }

  save(memory: SkillMemory): void {
    try {
      this.storage.setItem(STORAGE_KEY, JSON.stringify(toPersisted(memory)));
    } catch {
      // ベストエフォート。間隔記憶を失っても朱印・クリアは Progress 側で守られる。
    }
  }

  clear(): void {
    this.storage.removeItem(STORAGE_KEY);
  }
}

export class InMemorySkillMemoryRepository implements SkillMemoryRepository {
  private memory: SkillMemory = createSkillMemory();

  load(): SkillMemory {
    return this.memory;
  }

  save(memory: SkillMemory): void {
    this.memory = memory;
  }

  clear(): void {
    this.memory = createSkillMemory();
  }
}
